import { useEffect, useState } from 'react';
import { KeyRound, UserPlus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import PageHeader from '../components/shared/PageHeader';
import { supabase } from '../lib/supabase';
import { getOficinaId } from '../lib/office';
import { usePlanFeatures } from '../hooks/usePlanFeatures';

const emptyForm = { nome: '', email: '', telefone: '', senha: '' };
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Equipe() {
  const { loading: planLoading, features: planFeatures } = usePlanFeatures();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [mecanicos, setMecanicos] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const load = async () => {
    try {
      const oficinaId = await getOficinaId();
      if (!oficinaId) return;
      setOfficeId(oficinaId);
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      const [usersRes, ordensRes] = await Promise.all([
        supabase.from('usuarios').select('id,nome,email,telefone,ativo').eq('perfil', 'mecanico').eq('oficina_id', oficinaId).eq('ativo', true).order('nome'),
        supabase.from('ordens_servico').select('id,mecanico_id,status,valor_total,data_entrada').eq('oficina_id', oficinaId).gte('data_entrada', monthStart),
      ]);
      if (usersRes.error) throw usersRes.error;
      if (ordensRes.error) throw ordensRes.error;
      setMecanicos(usersRes.data || []);
      setOrdens(ordensRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar equipe:', error);
      toast.error('Não foi possível carregar a equipe');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  async function createOrUpdateAuthUser({ email, password, name }) {
    if (!password || password.length < 6) {
      throw new Error('Informe uma senha inicial com pelo menos 6 caracteres.');
    }

    const { data, error } = await supabase.functions.invoke('admin-create-user', {
      body: { email, password, name, perfil: 'mecanico' },
    });

    if (error || data?.error) {
      throw new Error(data?.error || error?.message || 'Não foi possível criar o login do mecânico.');
    }
  }

  const salvarMecanico = async () => {
    setFormError('');
    const email = form.email.trim().toLowerCase();
    const nome = form.nome.trim();

    if (!officeId) {
      const message = 'Oficina não identificada. Recarregue a página.';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (!nome || !email || !form.senha) {
      const message = 'Informe nome, email e senha inicial.';
      setFormError(message);
      toast.error(message);
      return;
    }

    if (!emailPattern.test(email)) {
      const message = 'Informe um email válido. Exemplo: mecanico@oficina.com.br';
      setFormError(message);
      toast.error(message);
      return;
    }

    setSaving(true);
    try {
      const { data: existingUser, error: existingError } = await supabase
        .from('usuarios')
        .select('id,oficina_id')
        .eq('email', email)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingUser && existingUser.oficina_id !== officeId) {
        throw new Error('Este email ja esta vinculado a outra oficina.');
      }

      await createOrUpdateAuthUser({ email, password: form.senha, name: nome });

      const payload = {
          oficina_id: officeId,
          nome,
          email,
          telefone: form.telefone.trim() || null,
          perfil: 'mecanico',
          ativo: true,
      };

      const { error } = existingUser
        ? await supabase.from('usuarios').update(payload).eq('id', existingUser.id).eq('oficina_id', officeId)
        : await supabase.from('usuarios').insert(payload);

      if (error) throw error;

      toast.success('Mecânico cadastrado com sucesso');
      setForm(emptyForm);
      setFormError('');
      setInviteOpen(false);
      load();
    } catch (error) {
      console.error('Erro ao cadastrar mecânico:', error);
      const message = error.message || 'Não foi possível cadastrar o mecânico';
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  async function sendPasswordReset() {
    if (!resetTarget?.email) return;
    setResetting(true);
    setResetLink('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-user-password', {
        body: {
          email: resetTarget.email,
          redirect_to: window.location.origin,
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Nao foi possivel gerar o reset de senha.');
      }

      setResetLink(data?.action_link || '');
      toast.success('Link de reset gerado.');
    } catch (error) {
      toast.error(error.message || 'Nao foi possivel gerar o reset de senha.');
    } finally {
      setResetting(false);
    }
  }

  const inviteModal = (
    <Modal open={inviteOpen} onClose={() => { setInviteOpen(false); setFormError(''); }} title="Convidar mecânico">
      <div className="space-y-3">
        <Input placeholder="Nome do mecânico" value={form.nome} onChange={(event) => setForm((prev) => ({ ...prev, nome: event.target.value }))} />
        <Input type="email" placeholder="mecanico@oficina.com.br" value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} />
        <Input mask="phone" placeholder="Telefone" value={form.telefone} onChange={(event) => setForm((prev) => ({ ...prev, telefone: event.target.value }))} />
        <Input type="password" placeholder="Senha inicial" value={form.senha} autoComplete="new-password" onChange={(event) => setForm((prev) => ({ ...prev, senha: event.target.value }))} />
        {formError && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {formError}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => { setInviteOpen(false); setFormError(''); }}>Cancelar</Button>
          <Button onClick={salvarMecanico} disabled={saving}>
            <UserPlus size={16} />
            {saving ? 'Salvando...' : 'Cadastrar mecânico'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (loading || planLoading) return <div className="flex h-64 items-center justify-center text-zinc-400">Carregando equipe...</div>;

  if (!planFeatures.teamManagement) {
    return (
      <div className="space-y-4">
        <PageHeader title="Equipe" subtitle="Disponível a partir do plano Pro" />
        <Card>
          <p className="font-semibold text-white">Gestao de equipe completa faz parte do Pro.</p>
          <p className="mt-2 text-sm leading-6 text-zinc-400">
            O Starter mantem o dono da oficina operando o sistema. Para cadastrar mecanicos, distribuir OS e acompanhar produtividade, atualize para o Pro.
          </p>
        </Card>
      </div>
    );
  }

  if (mecanicos.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader title="Equipe" subtitle="Mecanicos e produtividade" />
        <EmptyState icon={Users} title="Nenhum mecânico cadastrado" description="Convide sua equipe para acompanhar produtividade e OS atribuídas." actionText="Convidar mecânico" action={() => setInviteOpen(true)} />
        {inviteModal}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Equipe" subtitle="Desempenho e produtividade por mecânico" actions={<Button onClick={() => setInviteOpen(true)}><UserPlus size={16} /> Convidar</Button>} />
      {inviteModal}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {mecanicos.map((mecanico) => {
          const minhasOs = ordens.filter((ordem) => ordem.mecanico_id === mecanico.id);
          const finalizadas = minhasOs.filter((ordem) => ['finalizada', 'concluida', 'entregue'].includes(String(ordem.status || '').toLowerCase())).length;
          const produtividade = minhasOs.length ? Math.round((finalizadas / minhasOs.length) * 100) : 0;
          return (
            <Card key={mecanico.id}>
              <p className="font-semibold">{mecanico.nome}</p>
              <p className="text-sm text-zinc-400">{mecanico.email || mecanico.telefone || 'Sem contato'}</p>
              <p className="mt-3 text-sm text-zinc-400">OS do mês: <span className="font-semibold text-zinc-100">{minhasOs.length}</span></p>
              <p className="text-sm text-zinc-400">Produtividade: <span className="font-semibold text-orange-400">{produtividade}%</span></p>
              {mecanico.email && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-3"
                  onClick={() => {
                    setResetTarget(mecanico);
                    setResetLink('');
                  }}
                >
                  <KeyRound size={14} />
                  Resetar senha
                </Button>
              )}
            </Card>
          );
        })}
      </div>
      <Modal open={Boolean(resetTarget)} onClose={() => { setResetTarget(null); setResetLink(''); }} title="Resetar senha">
        <div className="space-y-4">
          <p className="text-zinc-300">Gerar link de nova senha para <strong>{resetTarget?.nome}</strong>?</p>
          {resetLink && (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
              <p className="text-sm font-semibold text-blue-200">Link gerado</p>
              <p className="mt-2 break-all text-xs text-blue-100">{resetLink}</p>
              <Button size="sm" variant="ghost" className="mt-3" onClick={() => navigator.clipboard?.writeText(resetLink)}>
                Copiar link
              </Button>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setResetTarget(null); setResetLink(''); }} disabled={resetting}>Fechar</Button>
            <Button onClick={sendPasswordReset} disabled={resetting}>
              <KeyRound size={15} />
              {resetting ? 'Gerando...' : 'Gerar link'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
